import {
  TDatasetFieldsConditions,
  TDatasetQueryParams,
} from '@infotech-soe/merps/types/Queries';
import {
  ActionName,
  CategoryName,
  DisplayModeName,
  IContextMenuItemRefreshRules,
  IInParamsValues,
  TDatabaseId,
  TDatasetName,
} from '@infotech-soe/metaschema';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { DatasetParamValues } from '../../infrastructure/providers/abstract/IApiProvider';
import {
  CategoryStateEntity,
  CategoryUI,
} from '../../models/category/category';
import DisplayModeConfig from '../../models/user-config/DisplayModeConfig';
import { CategoryLoadConfig } from '../../services/category/ICategoryService';
import CategoryViewModel from '../../services/category/view-model/CategoryViewModel';
import { DatasetLayoutViewModelType } from '../../services/category/view-model/DatasetLayoutViewModel';
import UniqueId from '../../services/uid/UniqueId';

import {
  emptyAction,
  failureAction,
  pendingAction,
  successAction,
} from './actionHandler';
import { DictionaryCloseReason } from './dictionary';

export interface CategoryTargetingInfo {
  relatedWithTree?: {
    entityId: TDatabaseId;
    treeId?: TDatabaseId;
  };
  treePos?: {
    displayMode: DisplayModeName;
    category: CategoryName;
    value: string;
  };
  datasetIds?: {
    [datasetName: string]: TDatabaseId;
  };
}

export type CategoryState = Record<UniqueId, CategoryStateEntity>;

const getInitialActiveTabs = (
  viewModel: CategoryViewModel
): Record<UniqueId, UniqueId> => {
  const activeTabs: Record<UniqueId, UniqueId> = {};

  for (const elementId of Object.keys(viewModel.layout.elements)) {
    const element = viewModel.layout.elements[elementId];

    if (element.type === DatasetLayoutViewModelType.TABBED) {
      activeTabs[elementId] = element.children[0];
    }
  }

  return activeTabs;
};

const categorySlice = createSlice({
  name: 'category',
  initialState: {} as CategoryState,
  reducers: {
    openDisplayMode: pendingAction<
      CategoryState,
      {
        category: CategoryName;
        displayMode?: DisplayModeName;
        ui: CategoryUI;
        inParams?: IInParamsValues;
        nextAction?: ActionName;
        withDefaultConfig?: boolean;
        pinned?: boolean;
      }
    >(),
    openDisplayModeSuccess: successAction<
      CategoryState,
      {
        result: CategoryViewModel;
        ui: CategoryUI;
        nextAction?: ActionName;
        inParams?: IInParamsValues;
        displayMode: string;
        displayModeConfig: DisplayModeConfig;
        pinned?: boolean;
      }
    >((state, { payload: { result, ui, nextAction } }) => {
      const category = result;

      state[category.id] = {
        category,
        activeTabs: getInitialActiveTabs(category),
        openMode: ui.mode,
      };
      state[category.id].nextAction = nextAction;
    }),
    openDisplayModeFailure: failureAction(),
    close: pendingAction<
      CategoryState,
      { categoryId: UniqueId; save?: boolean; reason?: DictionaryCloseReason }
    >(),
    closeSuccess: successAction<
      CategoryState,
      { categoryId: UniqueId; save: boolean; reason?: DictionaryCloseReason }
    >((state, { payload: { categoryId } }) => {
      delete state[categoryId];
    }),
    closeFailure: failureAction(),
    activateLayoutTab(
      state,
      {
        payload: { categoryId, tabsElementId, tabPaneElementId },
      }: PayloadAction<{
        categoryId: UniqueId;
        tabsElementId: UniqueId;
        tabPaneElementId: UniqueId;
      }>
    ) {
      state[categoryId].activeTabs[tabsElementId] = tabPaneElementId;
    },
    updateInParams(
      state,
      {
        payload: { categoryId, inParams, replace },
      }: PayloadAction<{
        categoryId: UniqueId;
        inParams: Record<string, any>;
        replace?: boolean;
      }>
    ) {
      const source = state[categoryId].category.source;

      if (replace) {
        source.inParams = inParams;
      } else {
        source.inParams = {
          ...source.inParams,
          ...inParams,
        };
      }
    },
    updateFieldsConditions(
      state,
      {
        payload: { categoryId, datasetName, fieldsConditions },
      }: PayloadAction<{
        categoryId: UniqueId;
        datasetName: TDatasetName;
        fieldsConditions: TDatasetFieldsConditions;
      }>
    ) {
      const category = state[categoryId].category;

      category.source.displayModeConditions[datasetName].fieldsConditions =
        fieldsConditions;
    },
    updateLocalConditions(
      state,
      {
        payload: { categoryId, datasetName, conditions },
      }: PayloadAction<{
        categoryId: UniqueId;
        datasetName: TDatasetName;
        conditions: Partial<TDatasetQueryParams>;
      }>
    ) {
      const category = state[categoryId].category;
      const { conditions: conds, queryOptions } = conditions;

      category.source.displayModeConditions[datasetName].conditions =
        conds ?? {};
      category.source.displayModeConditions[datasetName].queryOptions =
        queryOptions ?? {};
    },
    tracePositions: pendingAction<
      CategoryState,
      {
        categoryId: UniqueId;
        displayMode: string;
        inParams: IInParamsValues;
        treeActiveId?: number | null;
        forceDOMFocus?: true;
      }
    >(),
    tracePositionsSuccess: successAction<
      CategoryState,
      {
        categoryId: UniqueId;
        targetingInfo?: CategoryTargetingInfo;
        forceDOMFocus?: true;
      }
    >(),
    tracePositionsFailure: failureAction(),
    load: emptyAction<{
      categoryId: UniqueId;
      config?: CategoryLoadConfig;
    }>(),
    scheduleLoading(
      state,
      {
        payload: { categoryId, config },
      }: PayloadAction<{
        categoryId: UniqueId;
        config: CategoryLoadConfig;
      }>
    ) {
      state[categoryId].pendingLoad = config;
    },
    clearPendingLoad(
      state,
      {
        payload,
      }: PayloadAction<{
        categoryId: UniqueId;
      }>
    ) {
      delete state[payload.categoryId].pendingLoad;
    },
    refreshWithRules: emptyAction<{
      datasetId: UniqueId;
      refreshRules: IContextMenuItemRefreshRules;
      rowId?: TDatabaseId;
    }>(),
    refreshTree: emptyAction<{
      categoryId: UniqueId;
      params?: DatasetParamValues;
    }>(),
    resizeLayout(
      state,
      {
        payload: { categoryId, percent, elementId },
      }: PayloadAction<{
        categoryId: UniqueId;
        percent: number;
        elementId?: UniqueId;
      }>
    ) {
      if (elementId) {
        const element = state[categoryId].category.layout.elements[elementId];
        if (
          element.type === DatasetLayoutViewModelType.HORIZONTALLY_STACKED ||
          element.type === DatasetLayoutViewModelType.VERTICALLY_STACKED
        ) {
          element.panelSize = percent;
        }
      } else {
        state[categoryId].category.layout.treePanelSize = percent;
      }
    },
  },
});

export const categoryReducer = categorySlice.reducer;

export default categorySlice.actions;
